import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../../services/api';
import './TeachersPage.css';

const emptyForm = () => ({
    username: '',
    password: '',
    email: '',
    studentIds: [],
    levelIds: [],
    isActive: true,
});

const TeachersPage = () => {
    const [teachers, setTeachers] = useState([]);
    const [students, setStudents] = useState([]);
    const [areas, setAreas] = useState([]);
    const [levelsByArea, setLevelsByArea] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalMode, setModalMode] = useState(null); // null | 'create' | 'edit'
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const allLevelsFlat = useMemo(() => {
        const list = [];
        Object.entries(levelsByArea).forEach(([areaId, levels]) => {
            const area = areas.find((a) => String(a.id) === String(areaId));
            levels.forEach((lv) => {
                list.push({ ...lv, areaName: area?.name || '' });
            });
        });
        return list.sort((a, b) => `${a.areaName} ${a.name}`.localeCompare(`${b.areaName} ${b.name}`));
    }, [areas, levelsByArea]);

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [tRes, sRes, aRes] = await Promise.all([
                adminAPI.getTeachers(),
                adminAPI.getStudents(),
                adminAPI.getAreas(),
            ]);
            setTeachers(tRes.data.teachers || []);
            setStudents(sRes.data.students || []);
            const ars = aRes.data.areas || [];
            setAreas(ars);
            const levelMap = {};
            await Promise.all(
                ars.map(async (a) => {
                    const lr = await adminAPI.getLevels(a.id);
                    levelMap[a.id] = lr.data.levels || [];
                })
            );
            setLevelsByArea(levelMap);
            setError('');
        } catch (e) {
            setError(e.response?.data?.error || 'Ошибка загрузки');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const studentLabel = (id) => {
        const s = students.find((x) => x.id === id);
        if (!s) return id;
        return `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username;
    };

    const levelLabel = (id) => {
        const lv = allLevelsFlat.find((x) => x.id === id);
        return lv ? `${lv.areaName} — ${lv.name}` : id;
    };

    const openCreate = () => {
        setForm(emptyForm());
        setEditingId(null);
        setFormError('');
        setModalMode('create');
    };

    const openEdit = (t) => {
        setForm({
            username: t.username || '',
            password: '',
            email: t.email || '',
            studentIds: [...(t.studentIds || [])],
            levelIds: [...(t.levelIds || [])],
            isActive: t.is_active !== 0 && t.is_active !== false,
        });
        setEditingId(t.id);
        setFormError('');
        setModalMode('edit');
    };

    const closeModal = () => {
        setModalMode(null);
        setEditingId(null);
        setFormError('');
    };

    const toggleId = (id, key) => {
        const arr = form[key];
        const has = arr.includes(id);
        setForm({
            ...form,
            [key]: has ? arr.filter((x) => x !== id) : [...arr, id],
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        if (modalMode === 'create') {
            if (!form.username.trim() || !form.password) {
                setFormError('Укажите логин и пароль');
                return;
            }
        }
        if (form.studentIds.length === 0 || form.levelIds.length === 0) {
            setFormError('Выберите хотя бы одного ученика и один уровень');
            return;
        }

        try {
            setSaving(true);
            if (modalMode === 'create') {
                await adminAPI.createTeacher({
                    username: form.username.trim(),
                    password: form.password,
                    email: form.email.trim() || null,
                    studentIds: form.studentIds,
                    levelIds: form.levelIds,
                });
            } else if (modalMode === 'edit' && editingId) {
                const body = {
                    email: form.email.trim() || null,
                    isActive: form.isActive,
                    studentIds: form.studentIds,
                    levelIds: form.levelIds,
                };
                if (form.password && form.password.length > 0) {
                    body.password = form.password;
                }
                await adminAPI.updateTeacher(editingId, body);
            }
            closeModal();
            await loadAll();
        } catch (err) {
            setFormError(err.response?.data?.error || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (t) => {
        if (!window.confirm(`Удалить преподавателя «${t.username}»? Вход станет невозможен.`)) return;
        try {
            await adminAPI.deleteTeacher(t.id);
            await loadAll();
        } catch (err) {
            alert(err.response?.data?.error || 'Ошибка удаления');
        }
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container teachers-page">
            <div className="page-header">
                <div>
                    <h1>Преподаватели</h1>
                </div>
                <button
                    type="button"
                    className="btn-primary"
                    onClick={openCreate}
                    style={{
                        padding: '10px 18px',
                        background: '#1890ff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '14px',
                    }}
                >
                    + Создать преподавателя
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card">
                <table className="teachers-table">
                    <thead>
                        <tr>
                            <th>Логин</th>
                            <th>Email</th>
                            <th>Ученики</th>
                            <th>Уровни</th>
                            <th style={{ width: '140px' }}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {teachers.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center' }}>
                                    Пока нет преподавателей — нажмите «Создать преподавателя»
                                </td>
                            </tr>
                        ) : (
                            teachers.map((t) => (
                                <tr key={t.id}>
                                    <td>{t.username}</td>
                                    <td>{t.email || '—'}</td>
                                    <td className="teachers-cell-list">
                                        {(t.studentIds || []).map((id) => (
                                            <span key={id} className="tag">
                                                {studentLabel(id)}
                                            </span>
                                        ))}
                                    </td>
                                    <td className="teachers-cell-list">
                                        {(t.levelIds || []).map((id) => (
                                            <span key={id} className="tag tag-level">
                                                {levelLabel(id)}
                                            </span>
                                        ))}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            onClick={() => openEdit(t)}
                                            style={{
                                                marginRight: '8px',
                                                padding: '6px 10px',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                borderRadius: '6px',
                                                border: '1px solid #d9d9d9',
                                                background: '#fff',
                                            }}
                                        >
                                            Изменить
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(t)}
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                borderRadius: '6px',
                                                border: '1px solid #ffccc7',
                                                background: '#fff1f0',
                                                color: '#cf1322',
                                            }}
                                        >
                                            Удалить
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {modalMode && (
                <div
                    className="modal-overlay"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '16px',
                    }}
                    onClick={(ev) => ev.target === ev.currentTarget && closeModal()}
                    role="presentation"
                >
                    <div
                        className="teachers-modal card"
                        style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '20px 22px',
                            width: '100%',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>
                            {modalMode === 'create' ? 'Новый преподаватель' : 'Редактирование'}
                        </h2>
                        <form className="modal-form" onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '12px' }}>
                                <label className="form-label">Логин</label>
                                <input
                                    className="form-input"
                                    value={form.username}
                                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                                    disabled={modalMode === 'edit'}
                                    required={modalMode === 'create'}
                                    autoComplete="username"
                                />
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label className="form-label">
                                    {modalMode === 'create' ? 'Пароль' : 'Новый пароль (оставьте пустым, если не менять)'}
                                </label>
                                <input
                                    className="form-input"
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    required={modalMode === 'create'}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label className="form-label">Email</label>
                                <input
                                    className="form-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    autoComplete="email"
                                />
                            </div>
                            {modalMode === 'edit' && (
                                <div style={{ marginBottom: '12px' }}>
                                    <label className="checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={form.isActive}
                                            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                        />
                                        Активен (может войти)
                                    </label>
                                </div>
                            )}
                            <div style={{ marginBottom: '10px' }}>
                                <label className="form-label">Ученики</label>
                                <div className="checkbox-scroll">
                                    {students.length === 0 ? (
                                        <span style={{ color: '#999' }}>Нет учеников в системе</span>
                                    ) : (
                                        students.map((s) => (
                                            <label key={s.id} className="checkbox-row">
                                                <input
                                                    type="checkbox"
                                                    checked={form.studentIds.includes(s.id)}
                                                    onChange={() => toggleId(s.id, 'studentIds')}
                                                />
                                                {studentLabel(s.id)}
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label">Уровни (программы)</label>
                                <div className="checkbox-scroll">
                                    {allLevelsFlat.length === 0 ? (
                                        <span style={{ color: '#999' }}>Нет уровней</span>
                                    ) : (
                                        allLevelsFlat.map((lv) => (
                                            <label key={lv.id} className="checkbox-row">
                                                <input
                                                    type="checkbox"
                                                    checked={form.levelIds.includes(lv.id)}
                                                    onChange={() => toggleId(lv.id, 'levelIds')}
                                                />
                                                {lv.areaName} — {lv.name}
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                            {formError && (
                                <div className="error-message" style={{ marginBottom: '12px' }}>
                                    {formError}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={saving}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: '1px solid #d9d9d9',
                                        background: '#fff',
                                        cursor: saving ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        background: '#1890ff',
                                        color: '#fff',
                                        fontWeight: 600,
                                        cursor: saving ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {saving ? 'Сохранение…' : 'Сохранить'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeachersPage;
